<template>
  <!-- GNB 메뉴만 담는다. 마크업과 클래스는 partials/nav.html 그대로 바꾸지 않는다.
       header 껍데기와 분리해 둔 이유: 호스트 앱(frontend/main)은 자기 헤더를 쓰면서
       드롭다운만 이 컴포넌트로 가져다 쓴다. -->
  <div ref="root" :key="hydrationKey" v-html="nav" />
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRoute } from '#app'
import navMenu from '../js/nav-menu.js?raw'
import { useRenewalLocale, useRenewalHydrationKey } from '../composables/useRenewalText'
import { renewalNavHtml, applyNavInsights } from '../composables/useRenewalNav'
import { useRenewalInsights } from '../composables/useRenewalInsights'

const root = ref<HTMLElement | null>(null)
const route = useRoute()
const { locale } = useRenewalLocale()

// "최근 소식"은 마크업에 박아 두면 금방 낡는다. 데이터는 호스트가 주고
// (단독이면 자체 /api/insights) 마크업은 여기서 그대로 만든다.
// 홈 하단 "최근 인사이트"와 같이 뉴스만 싣는다 — 이벤트는 지난 날짜가 그대로 남아
// 메뉴에서 먼저 눈에 띄면 오히려 낡아 보인다.
const { data: insights } = await useRenewalInsights(3, ['news'])

const nav = computed(() => applyNavInsights(renewalNavHtml(locale.value), insights.value ?? [], locale.value))

// v-html 은 hydration 에서 교정되지 않는다 — 서버와 클라이언트 언어가 다를 때만 다시 그린다.
const hydrationKey = useRenewalHydrationKey()

// 메뉴 동작을 이 컴포넌트가 직접 건다.
// 페이지의 ClientInteractions 에 맡기면, 헤더가 지연 렌더되는 호스트 앱에서
// 스크립트가 먼저 돌아 [data-nav-item] 을 못 찾고 드롭다운이 죽는다.
const bindNavMenu = () => {
  new Function(navMenu)()
}

/**
 * 지금 보고 있는 페이지의 메뉴를 표시한다.
 *
 * 마크업에는 표시가 없다(partials/nav.html 의 주석 참고 — 관례가 없어 달지 않았다).
 * 여기서 현재 경로와 일치하는 링크를 찾아 `is-active` 를 붙이기만 하고, 어떻게 보일지는
 * 각 앱의 CSS 가 정한다. renewal 단독에는 `.is-active` 규칙이 없어 겉모습이 바뀌지 않는다.
 */
const normalize = (path: string) => path.replace(/\/+$/, '') || '/'

/**
 * 이 링크가 지금 보고 있는 페이지를 가리키는가.
 *
 * 상대경로는 그대로 비교하고, 절대 URL 은 **같은 호스트일 때만** 본다 —
 * 마크업에는 `https://codepresso.io/home/news` 처럼 운영 도메인을 직접 쓴 링크가 있고,
 * 운영에서는 그게 곧 현재 페이지다. 반대로 blog 같은 다른 서브도메인이
 * 루트(`/`)를 가리켜 홈에서 잘못 켜지는 것도 이 조건이 막는다.
 */
const pointsToCurrentPage = (anchor: Element, path: string) => {
  const href = anchor.getAttribute('href') ?? ''
  if (!href || href.startsWith('#')) return false
  if (href.startsWith('/')) return normalize(href) === path
  try {
    const url = new URL(href, window.location.href)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false
    if (url.hostname !== window.location.hostname) return false
    return normalize(url.pathname) === path
  } catch {
    return false
  }
}

const markActive = () => {
  const el = root.value
  if (!el) return
  const path = normalize(route.path)
  for (const item of el.querySelectorAll('[data-nav-item], .nav-item')) {
    const hit = [...item.querySelectorAll('a[href]')].some((a) => pointsToCurrentPage(a, path))
    item.classList.toggle('is-active', hit)
  }
  for (const entry of el.querySelectorAll('.nav-entry, .nav-link')) {
    entry.classList.toggle('is-active', pointsToCurrentPage(entry, path))
  }
}

onMounted(() => {
  bindNavMenu()
  markActive()
})

// 언어가 바뀌거나 hydration 후 다시 그리면 DOM 이 통째로 교체돼 리스너와 표시가 사라진다.
// nav-menu.js 는 중복 실행에 안전하므로 그때마다 다시 건다.
watch([hydrationKey, nav], () => nextTick(() => {
  bindNavMenu()
  markActive()
}))

// SPA 이동은 DOM 을 다시 만들지 않으므로 경로만 따로 좇는다.
watch(() => route.path, () => nextTick(markActive))
</script>
